import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MARKET_CODES } from "../../data/markets";

// IP country code → market slug
const COUNTRY_TO_MARKET = {
  US: "us",
  GB: "uk",
  AU: "au",
  AE: "uae",
  CA: "ca",
  DE: "de",
  JP: "jp",
  SG: "sg",
  BR: "br",
  IN: "in",
  FR: "fr",
  ES: "es",
  KR: "kr",
  TH: "th",
};

// Session flag — once we've redirected (or user opted to stay on global) within
// the current session, we don't redirect again.
const SESSION_FLAG = "appfillip_geo_handled";

/**
 * Multi-source country detection (CORS-friendly fallbacks).
 * Returns a 2-letter ISO country code (e.g. "US"), or null on failure.
 */
async function detectCountry() {
  const sources = [
    async () => {
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      if (!BACKEND) throw new Error("no-backend");
      const r = await fetch(`${BACKEND}/api/geo`);
      if (!r.ok) throw new Error("backend failed");
      const j = await r.json();
      return j?.country || null;
    },
    async () => {
      const r = await fetch("https://ipapi.co/country/");
      if (!r.ok) throw new Error("ipapi failed");
      return (await r.text()).trim();
    },
    async () => {
      const r = await fetch("https://get.geojs.io/v1/ip/country.json");
      if (!r.ok) throw new Error("geojs failed");
      const j = await r.json();
      return j?.country;
    },
    async () => {
      const r = await fetch("https://www.cloudflare.com/cdn-cgi/trace");
      if (!r.ok) throw new Error("cf failed");
      const t = await r.text();
      const m = t.match(/loc=([A-Z]{2})/);
      return m ? m[1] : null;
    },
  ];
  for (const src of sources) {
    try {
      const code = await src();
      if (code && /^[A-Z]{2}$/.test(code)) return code;
    } catch (_) { /* try next */ }
  }
  return null;
}

/**
 * Auto-redirects visitors landing on `/` to their localized market homepage
 * (e.g. USA IP → `/us`). One-shot per browser session. Silent — no UI.
 *
 * To bypass auto-redirect, append `?global=1` to the URL.
 */
export default function GeoBanner() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Only act on the global homepage
    if (location.pathname !== "/") return;

    // Respect explicit override
    if (new URLSearchParams(location.search).get("global") === "1") return;

    // Never redirect crawlers — Googlebot crawls from the US and would index
    // `/us` in place of the global homepage.
    if (/bot|crawl|spider|slurp|preview|headless/i.test(navigator.userAgent)) return;

    // Don't redirect if already on a market page (defensive — useLocation already gates above)
    if (MARKET_CODES.includes(location.pathname.slice(1))) return;

    // Already handled in this session — skip
    try {
      if (sessionStorage.getItem(SESSION_FLAG)) return;
    } catch (_) { /* ignore */ }

    let cancelled = false;
    (async () => {
      const code = await detectCountry();
      if (cancelled || !code) return;
      const slug = COUNTRY_TO_MARKET[code.toUpperCase()];
      if (!slug) return;
      try { sessionStorage.setItem(SESSION_FLAG, "1"); } catch (_) {}
      // Replace history so the back button doesn't ping-pong
      navigate(`/${slug}`, { replace: true });
    })();
    return () => { cancelled = true; };
  }, [location.pathname, location.search, navigate]);

  return null;
}
